import { useState } from "react";
import { api } from "@/lib/api";
import { useData } from "@/lib/data";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", contact_name: "", phone: "", address: "", note: "" };
const FIELDS = [["name", "Firma Adı *"], ["contact_name", "Yetkili Adı Soyadı"], ["phone", "Telefon"], ["address", "Adres"], ["note", "Not"]];

export default function SupplierCards({ orders, onChanged }) {
  const { suppliers, refreshSuppliers, refreshSoleCards } = useData();
  const refreshAll = () => { refreshSuppliers(); refreshSoleCards(); onChanged?.(); };
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);

  const openForm = (s) => { setEditingId(s?.id || null); setForm(s ? { ...empty, ...s } : empty); setOpen(true); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Firma adı zorunlu"); return; }
    const body = Object.fromEntries(FIELDS.map(([f]) => [f, form[f] || ""]));
    try {
      if (editingId) await api.put(`/suppliers/${editingId}`, body);
      else await api.post("/suppliers", body);
      toast.success(editingId ? "Firma güncellendi" : "Firma oluşturuldu");
      setOpen(false); refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Firma kartı silinsin mi?")) return;
    try { await api.delete(`/suppliers/${id}`); toast.success("Silindi"); refreshAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  const stats = (name) => {
    const os = orders.filter((o) => o.supplier === name);
    const ordered = os.reduce((a, o) => a + o.items.reduce((x, i) => x + i.qty, 0), 0);
    const got = os.reduce((a, o) => a + o.items.reduce((x, i) => x + (i.received_qty || 0), 0), 0);
    return { count: os.length, ordered, got };
  };

  return (
    <section className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5 mb-6" data-testid="supplier-cards-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl font-bold uppercase tracking-tight flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#B45309]" /> Taban Firmaları
        </h2>
        <Button onClick={() => openForm(null)} data-testid="supplier-new-btn" className="h-9 bg-[#D97706] hover:bg-[#B45309] text-white">
          <Plus className="w-4 h-4 mr-1" /> Yeni Firma
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {suppliers.length === 0 && <p className="text-sm text-[#8C7B68]">Henüz firma kartı yok.</p>}
        {suppliers.map((s) => {
          const st = stats(s.name);
          return (
            <div key={s.id} className="rounded-md border border-[#E8DDCA] bg-[#F5EFE4]/60 p-3" data-testid={`supplier-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="font-semibold truncate">{s.name}</p>
                  <p className="text-[11px] text-[#8C7B68]">{[s.contact_name, s.phone].filter(Boolean).join(" · ") || "—"}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => openForm(s)} data-testid={`supplier-edit-${s.id}`} className="text-[#8C7B68] hover:text-[#B45309]"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(s.id)} data-testid={`supplier-delete-${s.id}`} className="text-[#8C7B68] hover:text-[#EF4444]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-[11px] font-mono mt-2" data-testid={`supplier-stats-${s.id}`}>
                {st.count} sipariş · verilen {st.ordered} · gelen <span className="text-[#047857]">{st.got}</span> · kalan <span className="text-[#B45309] font-bold">{st.ordered - st.got}</span>
              </p>
            </div>
          );
        })}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFBF4] border-[#DCCFB8] text-[#2B2118] max-w-lg" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{editingId ? "Firmayı Düzenle" : "Yeni Taban Firması"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FIELDS.map(([f, l]) => (
              <div key={f}>
                <Label className="text-xs text-[#6B5B4A]">{l}</Label>
                <Input value={form[f] || ""} data-testid={`supplier-${f}`} onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                  className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={submit} data-testid="supplier-submit-btn" className="bg-[#D97706] hover:bg-[#B45309] text-white h-11 px-6">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
